import React, { useState } from 'react';
import { StickyNote } from '../../types';
import MergeProposalModal from '../MergeProposalModal';
import { useWhiteboardStore } from '../../store/useWhiteboardStore';

interface MobileNoteCardProps {
  note: StickyNote;
  onClick: () => void;
  onDoubleClick: () => void;
  onDelete: () => void;
  onColorChange: (color: string) => void;
  isRoot: boolean;
}

const MobileNoteCard: React.FC<MobileNoteCardProps> = ({
  note,
  onClick,
  onDoubleClick,
  onDelete,
  onColorChange,
  isRoot
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);

  // Get status badge configuration
  const getStatusBadge = () => {
    const status = note.status || (isRoot ? 'root' : 'active');
    
    switch (status) {
      case 'root':
        return {
          text: 'Root',
          className: 'bg-blue-500 text-white'
        };
      case 'active':
        return {
          text: 'Active',
          className: 'bg-yellow-400 text-white'
        };
      case 'merged':
        return {
          text: 'Merged',
          className: 'bg-green-500 text-white'
        };
      case 'archived':
        return {
          text: 'Archived',
          className: 'bg-gray-500 text-white'
        };
      default:
        return {
          text: 'Active',
          className: 'bg-yellow-400 text-white'
        };
    }
  };

  // Get background color based on note color
  const getBackgroundColor = () => {
    if (!note.color) {
      return {
        backgroundColor: 'rgba(252, 231, 243, 0.8)',
        borderColor: 'rgba(251, 207, 232, 0.5)'
      };
    }
    
    // Convert hex color to rgba with transparency
    const hexToRgba = (hex: string, alpha: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    
    return {
      backgroundColor: hexToRgba(note.color, 0.8),
      borderColor: hexToRgba(note.color, 0.5)
    };
  };

  // Truncate text for display
  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Get display title
  const getDisplayTitle = () => {
    if (note.topic && note.topic.trim()) {
      return truncateText(note.topic, 30);
    }
    
    // Extract title from content if no topic
    const contentLines = note.content.split('\n');
    const firstLine = contentLines[0]?.trim();
    if (firstLine && firstLine.length > 0) {
      return truncateText(firstLine, 30);
    }
    
    return isRoot ? 'Main Thread' : 'New Branch';
  };

  // Get display subtitle
  const getDisplaySubtitle = () => {
    if (note.persona && note.persona.trim()) {
      return note.persona;
    }
    
    // Show content preview if no persona
    if (note.content && note.content.trim()) {
      const contentLines = note.content.split('\n');
      const firstLine = contentLines[0]?.trim();
      if (firstLine && firstLine.length > 0 && firstLine !== note.topic) {
        return truncateText(firstLine, 40);
      }
    }
    
    return isRoot ? 'Project Lead' : 'Team Member';
  };

  const statusBadge = getStatusBadge();
  const backgroundColor = getBackgroundColor();

  // Button handlers
  const handleProposeMerge = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMergeModal(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    onDelete();
    setShowDeleteConfirm(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleColorPickerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowColorPicker(!showColorPicker);
  };

  const handleColorSelect = (color: string) => {
    onColorChange(color);
    setShowColorPicker(false);
  };

  const handleMerge = (summary: string, mergeTarget: StickyNote) => {
    // Use the same merge logic as the desktop version
    const { mergeBranchToParent, updateNote } = useWhiteboardStore.getState();
    mergeBranchToParent(note.id);
    
    updateNote(note.id, { 
      showConverge: true,
      showConvergeGlow: true,
      mergeTargetId: mergeTarget.id
    });
    
    // Add the summary to the merge target note's chat history
    const targetNote = useWhiteboardStore.getState().state.notes.find(n => n.id === mergeTarget.id);
    if (targetNote) {
      const currentChatHistory = targetNote.chatHistory || [];
      const newMessageIndex = currentChatHistory.length;
      const updatedChatHistory = [
        ...currentChatHistory,
        { sender: 'ai' as const, text: summary }
      ];
      
      const currentMergeMessages = targetNote.mergeMessages || [];
      const updatedMergeMessages = [
        ...currentMergeMessages,
        {
          messageIndex: newMessageIndex,
          sourceBranchId: note.id,
          sourceBranchName: note.topic || 'Unknown Branch'
        }
      ];
      
      updateNote(mergeTarget.id, {
        chatHistory: updatedChatHistory,
        mergeMessages: updatedMergeMessages
      });
    }
    
    setShowMergeModal(false);
  };

  return (
    <div
      className="mobile-note-card"
      style={backgroundColor}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      {/* Status Badge and Merge Button */}
      <div className="mobile-status-container">
        <div className={`mobile-status-badge ${statusBadge.className}`}>
          {statusBadge.text}
        </div>
        
        {/* Merge Button - only for branch notes, next to status badge */}
        {!isRoot && note.parentId && !note.showConverge && (
          <button
            className="mobile-merge-btn"
            onClick={handleProposeMerge}
            title="Propose merge with parent"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <circle cx="7" cy="6" r="2" stroke="white" strokeWidth="2" fill="none"/>
              <circle cx="7" cy="18" r="2" stroke="white" strokeWidth="2" fill="none"/>
              <circle cx="17" cy="12" r="2" stroke="white" strokeWidth="2" fill="none"/>
              <path d="M7 8v8a5 5 0 0 0 5 5h5" stroke="white" strokeWidth="2" fill="none"/>
              <path d="M7 8V6" stroke="white" strokeWidth="2" fill="none"/>
              <path d="M17 14v-2a5 5 0 0 0-5-5H7" stroke="white" strokeWidth="2" fill="none"/>
            </svg>
            <span>Merge</span>
          </button>
        )}
      </div>
      
      {/* Title */}
      <div className="mobile-note-title">
        {getDisplayTitle()}
      </div>
      
      {/* Subtitle */}
      <div className="mobile-note-subtitle">
        {getDisplaySubtitle()}
      </div>
      
      {/* Content Preview (if available and different from title) */}
      {note.content && note.content.trim() && (
        <div className="mobile-note-content">
          {truncateText(note.content, 80)}
        </div>
      )}
      
      {/* Action Buttons - Ordered: [close, color picker] */}
      <div className="mobile-note-actions">
        {/* Delete Button */}
        <button
          className="mobile-action-btn delete-btn"
          onClick={handleDelete}
          title={isRoot ? "Delete idea starter" : "Delete branch"}
        >
          ✕
        </button>

        {/* Color Picker Button */}
        <button
          className="mobile-action-btn color-picker-btn"
          onClick={handleColorPickerClick}
          title="Change note color"
        >
          🖌️
        </button>
      </div>
      
      {/* Level Indicator (for debugging) */}
      {process.env.NODE_ENV === 'development' && note.level !== undefined && (
        <div className="mobile-note-level">
          Level: {note.level}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="mobile-delete-modal-overlay" onClick={handleCancelDelete}>
          <div className="mobile-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{isRoot ? 'Delete Idea Starter' : 'Delete Branch'}</h3>
            <p>Are you sure you want to delete this {isRoot ? 'idea starter' : 'branch'}?</p>
            <div className="mobile-delete-modal-actions">
              <button className="mobile-delete-cancel" onClick={handleCancelDelete}>
                Cancel
              </button>
              <button className="mobile-delete-confirm" onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Color Picker Modal */}
      {showColorPicker && (
        <div className="mobile-color-picker-overlay" onClick={() => setShowColorPicker(false)}>
          <div className="mobile-color-picker-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Choose Color</h3>
            <div className="mobile-color-picker-grid">
              {[
                { id: 'yellow', name: 'Yellow', solid: '#FFFDE7' },
                { id: 'orange', name: 'Orange', solid: '#FFF3E0' },
                { id: 'red', name: 'Red', solid: '#FFEBEE' },
                { id: 'pink', name: 'Pink', solid: '#FCE4EC' },
                { id: 'purple', name: 'Purple', solid: '#F3E5F5' },
                { id: 'indigo', name: 'Indigo', solid: '#E8EAF6' },
                { id: 'blue', name: 'Blue', solid: '#E3F2FD' },
                { id: 'cyan', name: 'Cyan', solid: '#E1F5FE' },
                { id: 'teal', name: 'Teal', solid: '#E0F2F1' },
                { id: 'green', name: 'Green', solid: '#E8F5E9' },
                { id: 'lime', name: 'Lime', solid: '#F9FBE7' },
                { id: 'white', name: 'White', solid: '#FFFFFF' }
              ].map((color) => (
                <button
                  key={color.id}
                  className="mobile-color-option"
                  style={{ backgroundColor: color.solid }}
                  onClick={() => handleColorSelect(color.solid)}
                  title={color.name}
                >
                  {note.color === color.solid && (
                    <div className="mobile-color-selected">✓</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Merge Proposal Modal */}
      {showMergeModal && note.parentId && (
        <MergeProposalModal
          branchNote={note}
          parentNote={useWhiteboardStore.getState().state.notes.find(n => n.id === note.parentId)!}
          isOpen={showMergeModal}
          onClose={() => setShowMergeModal(false)}
          onMerge={handleMerge}
        />
      )}
    </div>
  );
};

export default MobileNoteCard;
